/**
 * Navigatie, inloggen en uitloggen om de beheerpagina's heen (SPEC §7).
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { api, type Gebruikersregel, type OmgevingInfo } from '../../api';
import { Knop, Kroon } from '../../componenten/basis';
import { rollenAlsZin } from '../../lib/rollen';
import { Inlogformulier } from '../Inloggen';

export function Beheerschil({ omgeving, bereikbaar, ververs }: { omgeving: OmgevingInfo; bereikbaar: boolean; ververs: () => Promise<void> }) {
  if (!bereikbaar) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="text-2xl">Het beheer is even niet bereikbaar</h1>
        <p className="mt-3 text-inkt-zacht">Dit ligt niet aan je inlog. Probeer het zo nog eens.</p>
        <Knop soort="stil" onClick={() => window.location.reload()} className="mt-6">
          Opnieuw proberen
        </Knop>
      </div>
    );
  }

  if (!omgeving.gebruiker) {
    return (
      <>
        <div className="mx-auto max-w-md px-4 py-16">
          <Inlogformulier soort="beheer" omgeving={omgeving} />
          {omgeving.testmodus && omgeving.lokaal && <Testinlog ververs={ververs} />}
          <p className="mt-6 text-center text-sm">
            <Link to="/" className="text-inkt-zacht">
              ← Naar de muur
            </Link>
          </p>
        </div>
      </>
    );
  }

  const tab = ({ isActive }: { isActive: boolean }) =>
    `rounded-full px-3 py-1.5 text-sm no-underline transition ${isActive ? 'bg-vlak-hoog text-goud-300' : 'text-inkt-zacht hover:text-inkt'}`;

  return (
    <>
      <header className="border-b border-lijn-zacht bg-vlak/60 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link to="/beheer" className="flex items-center gap-2 text-inkt no-underline">
            <Kroon className="h-6 w-6 text-goud-400" />
            <span className="font-display text-lg">Helden · beheer</span>
          </Link>
          <nav className="flex flex-wrap items-center gap-1">
            <NavLink to="/beheer" end className={tab}>
              Overzicht
            </NavLink>
            <NavLink to="/beheer/helden" className={tab}>
              Helden
            </NavLink>
            <NavLink to="/beheer/instellingen" className={tab}>
              Instellingen
            </NavLink>
            <Link to="/" className="rounded-full px-3 py-1.5 text-sm text-inkt-zacht no-underline hover:text-inkt">
              Muur ↗
            </Link>
          </nav>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-inkt-zacht sm:inline" title={rollenAlsZin(omgeving.gebruiker.rollen)}>
              {omgeving.gebruiker.naam}
            </span>
            <Knop
              soort="stil"
              klein
              onClick={async () => {
                await api.stuur('/api/uitloggen', { soort: 'beheer' });
                window.location.href = '/beheer';
              }}
            >
              Uitloggen
            </Knop>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <Outlet />
      </main>
    </>
  );
}

function Testinlog({ ververs }: { ververs: () => Promise<void> }) {
  const [gebruikers, zetGebruikers] = useState<Gebruikersregel[]>([]);
  useEffect(() => {
    api.haal<Gebruikersregel[]>('/api/testgebruikers').then(zetGebruikers).catch(() => undefined);
  }, []);
  if (gebruikers.length === 0) return null;
  return (
    <div className="mx-auto mt-6 max-w-md rounded-xl border border-dashed border-test/40 p-4 text-sm">
      <p className="text-test">Testmodus: inloggen zonder mailbox</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {gebruikers
          .filter((g) => g.actief)
          .map((g) => (
            <button
              key={g.id}
              type="button"
              className="rounded-full border border-lijn px-3 py-1 text-inkt-zacht hover:border-test hover:text-inkt"
              onClick={async () => {
                await api.stuur('/api/testlogin', { gebruikerId: g.id });
                await ververs();
              }}
            >
              {g.naam} <span className="text-inkt-stil">· {rollenAlsZin(g.rollen)}</span>
            </button>
          ))}
      </div>
    </div>
  );
}
